// src/pages/CouponManagePage.tsx
import * as React from "react";
import useSWR from "swr";
import Swal from "sweetalert2";
import { Card, Input, Pagination, Select, Space, Spin, Typography } from "antd";

import { snackbarStore } from "src/store/snackbarStore";
import CustomButton from "src/components/common/CustomButton";
import CouponTable from "src/components/coupon/CouponTable";
import CouponUpsertDialog from "src/components/coupon/CouponUpsertDialog";

import {
  getCouponListApi,
  getCouponDetailApi,
  createCouponApi,
  updateCouponApi,
  deleteCouponApi,
  updateCouponStatusApi,
  type CouponListItem,
  type CouponDetail,
  type CouponUpsertPayload,
} from "src/api/coupon/couponApi";

const SIZE_OPTIONS = [20, 50, 100];

export default function CouponManagePage() {
  const [couponName, setCouponName] = React.useState("");
  const [couponCode, setCouponCode] = React.useState("");
  const [deleted, setDeleted] = React.useState<boolean>(false);
  const [page, setPage] = React.useState(1);
  const [size, setSize] = React.useState(20);
  const [order] = React.useState("createdAt,desc");

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editItem, setEditItem] = React.useState<CouponListItem | null>(null);
  const [detail, setDetail] = React.useState<CouponDetail | null>(null);
  const [copyDetail, setCopyDetail] = React.useState<CouponDetail | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);

  const swrKey = React.useMemo(
    () => ["coupon/list", couponName, couponCode, deleted, page, size, order] as const,
    [couponName, couponCode, deleted, page, size, order]
  );

  const { data, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      const apiPage = Math.max(0, page - 1);
      return await getCouponListApi({
        couponName: couponName.trim() || undefined,
        couponCode: couponCode.trim() || undefined,
        deleted,
        page: apiPage,
        size,
        order,
      });
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const list = data?.data?.content ?? [];
  const totalElements = data?.data?.page?.totalElements ?? 0;

  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!dialogOpen || !editItem?.couponId) {
        setDetail(null);
        return;
      }
      try {
        setDetailLoading(true);
        const res = await getCouponDetailApi(editItem.couponId);
        if (!alive) return;
        if (res.statusCode === 1) setDetail(res.data);
        else setDetail(null);
      } finally {
        if (alive) setDetailLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [dialogOpen, editItem?.couponId]);

  const openCreate = () => {
    setEditItem(null);
    setCopyDetail(null);
    setDialogOpen(true);
  };

  const openEdit = (item: CouponListItem) => {
    setEditItem(item);
    setCopyDetail(null);
    setDialogOpen(true);
  };

  const openCopy = async (item: CouponListItem) => {
    try {
      setDetailLoading(true);
      const res = await getCouponDetailApi(item.couponId);
      if (res.statusCode === 1 && res.data) {
        setEditItem(null);
        setCopyDetail(res.data);
        setDialogOpen(true);
      } else {
        snackbarStore.getState().addSnackbar({
          message: res.message || "쿠폰 정보를 불러올 수 없습니다.",
          severity: "error",
        });
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditItem(null);
    setDetail(null);
    setCopyDetail(null);
  };

  const onSubmit = async (payload: CouponUpsertPayload): Promise<void> => {
    try {
      setSaving(true);

      if (editItem?.couponId) {
        await updateCouponApi(editItem.couponId, payload);
      } else {
        await createCouponApi(payload);
      }

      // 성공 시 (인터셉터가 이미 성공 snackbar를 띄움)
      closeDialog();
      await mutate();
    } catch {
      // 실패 시 인터셉터가 이미 에러 snackbar를 띄움
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (item: CouponListItem) => {
    const result = await Swal.fire({
      title: "삭제 확인",
      text: `"${item.couponName ?? item.couponCode}" 삭제할까요?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "삭제",
      cancelButtonText: "취소",
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) {
          container.style.zIndex = "2000";
        }
      },
    });
    if (!result.isConfirmed) return;

    try {
      setSaving(true);
      const res = await deleteCouponApi(item.couponId);
      if (res.statusCode !== 1) {
        snackbarStore.getState().addSnackbar({
          message: res.message || "삭제 실패",
          severity: "error",
        });
        return;
      }

      snackbarStore.getState().addSnackbar({ message: "삭제 완료", severity: "success" });
      await mutate();
    } finally {
      setSaving(false);
    }
  };

  const onToggleActive = async (item: CouponListItem) => {
    const nextActive = !item.isActive;
    try {
      setSaving(true);
      const res = await updateCouponStatusApi(item.couponId, nextActive);
      if (res.statusCode !== 1) {
        snackbarStore.getState().addSnackbar({
          message: res.message || "상태 변경 실패",
          severity: "error",
        });
        return;
      }

      snackbarStore.getState().addSnackbar({
        message: "상태가 변경되었습니다.",
        severity: "success",
      });
      await mutate();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={{ padding: 24, width: "100%" }}>
        <Card style={{ borderRadius: 12 }} bordered>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div>
              <Typography.Title level={4} style={{ fontWeight: 900, marginBottom: 4 }}>
                쿠폰 목록
              </Typography.Title>
              <Typography.Text type="secondary">
                총 {totalElements.toLocaleString()}건
              </Typography.Text>
            </div>

            <CustomButton variant="contained" onClick={openCreate} disabled={saving}>
              등록
            </CustomButton>
          </div>

          <Space wrap size={12} align="center" style={{ marginBottom: 16 }}>
            <Input
              value={couponName}
              onChange={(e) => setCouponName(e.target.value)}
              placeholder="쿠폰명"
              style={{ width: 240 }}
            />
            <Input
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder="쿠폰코드"
              style={{ width: 240 }}
            />
            <Select
              value={deleted ? "true" : "false"}
              onChange={(value) => {
                setDeleted(value === "true");
                setPage(1);
              }}
              style={{ width: 160 }}
              options={[
                { value: "false", label: "삭제 안됨" },
                { value: "true", label: "삭제됨" },
              ]}
            />
            <CustomButton variant="contained" onClick={() => mutate()} disabled={isLoading}>
              검색
            </CustomButton>

            <div style={{ flex: 1 }} />

            <Select
              value={size}
              onChange={(value) => {
                setSize(Number(value));
                setPage(1);
              }}
              style={{ width: 140 }}
              options={SIZE_OPTIONS.map((s) => ({ value: s, label: `${s}개` }))}
            />
          </Space>

          {isLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
              <Spin />
            </div>
          ) : (
            <>
              <CouponTable
                list={list}
                loading={false}
                disabled={saving || detailLoading}
                onEdit={openEdit}
                onCopy={openCopy}
                onDelete={onDelete}
                onToggleActive={onToggleActive}
              />

              <div style={{ display: "flex", justifyContent: "center", paddingTop: 16 }}>
                <Pagination
                  current={page}
                  total={Math.max(1, totalElements)}
                  pageSize={size}
                  onChange={(nextPage) => setPage(nextPage)}
                  showSizeChanger={false}
                  disabled={isLoading || saving}
                />
              </div>
            </>
          )}
        </Card>
      </div>

      <CouponUpsertDialog
        open={dialogOpen}
        item={detail || copyDetail}
        listItem={editItem}
        loading={saving || detailLoading}
        onClose={closeDialog}
        onSubmit={onSubmit}
      />
    </>
  );
}
